/** Billing (C06-001..007): Stripe events, one catalogue, payment ledger + reconciliation, invoices, subscriptions/dunning, usage, money display. */
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { Button, Heading, Panel, Text } from '@qm/ui';
import type { CatalogueItem } from '@qm/contracts/billing';
import { useSurface } from '../kit/context';
import { useAction, useResource } from '../kit/resource';
import { Badge, DataTable, ErrorNote, Field, Loadable, Money, Notice, Screen, Stat, Tabs, When } from '../kit/ui';
import type { RevenueView } from '../api';
import { canSeeRevenue, dunningLabel, isFinance, reconMismatches } from '../lib/logic';
import { useAdminApi } from './layout';

const TABS = ['ledger', 'events', 'catalogue', 'invoices', 'subscriptions', 'usage'] as const;

export function BillingPage() {
  const { t, roles } = useSurface();
  const [sp, setSp] = useSearchParams();
  const tab = (TABS as readonly string[]).includes(sp.get('tab') ?? '') ? sp.get('tab')! : 'ledger';
  if (!canSeeRevenue(roles)) return <Screen title={t('portal.admin.billing.title')}><Notice testId="finance-only">{t('portal.admin.financeOnly')}</Notice></Screen>;
  return (
    <Screen title={t('portal.admin.billing.title')} testId="billing">
      <Tabs label={t('portal.admin.billing.title')} active={tab} onSelect={(v) => setSp({ tab: v })} tabs={TABS.map((x) => ({ id: x, label: t(`portal.admin.billing.tab.${x}`) }))} />
      {tab === 'events' ? <StripeEvents /> : tab === 'catalogue' ? <Catalogue /> : tab === 'invoices' ? <StaffInvoices /> : tab === 'subscriptions' ? <Subscriptions /> : tab === 'usage' ? <UsageCounters /> : <Ledger />}
    </Screen>
  );
}

function paidTotals(r: RevenueView): { amountMinor: number; currency: string }[] {
  return Array.isArray(r.paid) ? r.paid : [r.paid];
}

/** Revenue comes only from the payment ledger; complimentary grants are counted separately at $0 (C06-003). */
export function Ledger() {
  const { t, roles } = useSurface();
  const api = useAdminApi();
  const revenue = useResource('billing:revenue', () => api.revenue());
  const runs = useResource('billing:recon', api.reconRuns);
  const alerts = useResource('billing:alerts', api.billingAlerts);
  const [date, setDate] = useState('');
  const run = useAction(api.runReconciliation);
  return (
    <div data-testid="ledger">
      <Loadable resource={revenue}>
        {(r) => (
          <div className="qx-grid">
            {paidTotals(r).map((p) => <Stat key={p.currency} testId={`revenue-${p.currency}`} label={t('portal.admin.billing.paidRevenue')} value={<Money minor={p.amountMinor} currency={p.currency} />} />)}
            <Stat testId="complimentary-grants" label={t('portal.admin.billing.complimentaryGrants')} value={t('portal.admin.billing.grantsValue', { n: r.complimentary.grants })} />
          </div>
        )}
      </Loadable>
      <Text tone="muted">{t('portal.admin.billing.ledgerNote')}</Text>
      <Loadable resource={alerts}>
        {(rows) => (rows.length ? <Notice tone="bad" testId="recon-alerts">{rows.map((a) => <span key={a.id} data-ref={a.ref}>{t('portal.admin.billing.alert', { ref: a.ref, msg: a.message })} </span>)}</Notice> : null)}
      </Loadable>
      {isFinance(roles) ? (
        <form className="qx-toolbar" onSubmit={async (e) => { e.preventDefault(); if (date && (await run.run(date))) { runs.reload(); alerts.reload(); } }}>
          <Field label={t('portal.admin.billing.reconDate')}>{(id) => <input id={id} type="date" value={date} onChange={(e) => setDate(e.target.value)} />}</Field>
          <Button type="submit" disabled={!date || run.pending}>{t('portal.admin.billing.runRecon')}</Button>
        </form>
      ) : null}
      <ErrorNote error={run.error} />
      <Loadable resource={runs}>
        {(rows) => <DataTable rows={rows} rowKey={(r) => r.id} empty={t('portal.kit.empty')} columns={[
          { key: 'd', header: t('portal.admin.billing.date'), render: (r) => r.run_date },
          { key: 'm', header: t('portal.admin.billing.matched'), render: (r) => `${r.matched}/${r.stripe_count}` },
          { key: 's', header: t('portal.admin.status'), render: (r) => <Badge tone={r.status === 'matched' ? 'good' : 'bad'} testId={`recon-${r.status}`}>{t(`portal.admin.billing.recon.${r.status}`)}</Badge> },
          { key: 'x', header: t('portal.admin.billing.mismatches'), render: (r) => reconMismatches(r).map((m) => m.ref).join(', ') || '-' },
        ]} />}
      </Loadable>
    </div>
  );
}

function StripeEvents() {
  const { t } = useSurface();
  const api = useAdminApi();
  const events = useResource('billing:events', () => api.stripeEvents({}));
  return (
    <Loadable resource={events}>
      {(rows) => <DataTable rows={rows} rowKey={(e) => e.id} empty={t('portal.kit.empty')} columns={[
        { key: 'id', header: t('portal.admin.billing.eventId'), render: (e) => <code>{e.eventId}</code> },
        { key: 'ty', header: t('portal.admin.billing.eventType'), render: (e) => e.type },
        { key: 's', header: t('portal.admin.status'), render: (e) => <Badge tone={e.status === 'processed' ? 'good' : e.status === 'failed' ? 'bad' : 'neutral'} testId={`event-${e.status}`}>{t(`portal.admin.billing.event.${e.status}`)}</Badge> },
        { key: 'a', header: t('portal.admin.billing.attempts'), render: (e) => e.attempts },
        { key: 'r', header: t('portal.admin.billing.received'), narrow: 'hide', render: (e) => <When iso={e.receivedAt} /> },
      ]} />}
    </Loadable>
  );
}

function Catalogue() {
  const { t } = useSurface();
  const api = useAdminApi();
  const cat = useResource('billing:catalogue', async () => { const r = await api.catalogue(); return Array.isArray(r) ? r : r.items; });
  return (
    <div data-testid="catalogue">
      <Text tone="muted">{t('portal.admin.billing.catalogueNote')}</Text>
      <Loadable resource={cat}>
        {(rows) => <DataTable<CatalogueItem> rows={rows} rowKey={(i) => i.sku} empty={t('portal.kit.empty')} columns={[
          { key: 'sku', header: 'SKU', render: (i) => <code>{i.sku}</code> },
          { key: 'n', header: t('portal.admin.billing.product'), render: (i) => i.name },
          { key: 'p', header: t('portal.admin.billing.price'), render: (i) => <Money minor={i.price.amountMinor} currency={i.price.currency} /> },
          { key: 'k', header: t('portal.admin.billing.kind'), render: (i) => i.kind },
          { key: 's', header: t('portal.admin.billing.sellable'), render: (i) => (i.sellable ? <Badge tone="good">{t('portal.admin.yes')}</Badge> : <Badge tone="warn">{t('portal.admin.billing.awaitingApproval')}</Badge>) },
        ]} />}
      </Loadable>
    </div>
  );
}

/** A complimentary grant is shown as a $0 grant, never as a payment (C06-004 edge). */
function StaffInvoices() {
  const { t } = useSurface();
  const api = useAdminApi();
  const [tenant, setTenant] = useState('');
  const list = useResource(`billing:invoices:${tenant}`, () => api.staffInvoices(tenant || undefined));
  return (
    <div data-testid="staff-invoices">
      <Field label={t('portal.admin.impersonation.tenant')}>{(id) => <input id={id} value={tenant} onChange={(e) => setTenant(e.target.value.trim())} />}</Field>
      <Loadable resource={list}>
        {(rows) => <DataTable rows={rows} rowKey={(i) => i.id} empty={t('portal.kit.empty')} columns={[
          { key: 'n', header: t('portal.admin.invoice.number'), render: (i) => i.number },
          { key: 'k', header: t('portal.admin.billing.kind'), render: (i) => (i.kind === 'grant' ? <Badge tone="accent" testId="invoice-grant">{t('customers.orders.complimentary')}</Badge> : <Badge tone="good">{t('portal.admin.billing.payment')}</Badge>) },
          { key: 'a', header: t('portal.admin.invoice.total'), render: (i) => <Money minor={i.kind === 'grant' ? 0 : i.totalMinor ?? 0} currency={i.currency} /> },
          { key: 's', header: t('portal.admin.status'), render: (i) => i.status },
          { key: 'd', header: t('portal.admin.billing.date'), narrow: 'hide', render: (i) => <When iso={i.issuedAt} /> },
        ]} />}
      </Loadable>
    </div>
  );
}

function Subscriptions() {
  const { t, now } = useSurface();
  const api = useAdminApi();
  const [id, setId] = useState('');
  const [lookup, setLookup] = useState('');
  const sub = useResource(`billing:sub:${lookup}`, () => (lookup ? api.subscription(lookup) : Promise.resolve(null)));
  return (
    <div data-testid="subscriptions">
      <form className="qx-toolbar" onSubmit={(e) => { e.preventDefault(); setLookup(id.trim()); }}>
        <Field label={t('portal.admin.billing.subscriptionId')}>{(fid) => <input id={fid} value={id} onChange={(e) => setId(e.target.value)} />}</Field>
        <Button type="submit">{t('portal.admin.search')}</Button>
      </form>
      <Loadable resource={sub}>
        {(s) => (s ? (
          <Panel>
            <Heading level={2}>{s.planSku}</Heading>
            <Badge testId={`dunning-${dunningLabel(s, now())}`}>{t(`portal.admin.billing.dunning.${dunningLabel(s, now())}`)}</Badge>
            <Text>{t('portal.admin.billing.periodEnd')}: <When iso={s.currentPeriodEnd} /></Text>
            {s.graceEndsAt ? <Text>{t('portal.admin.billing.graceEnds')}: <When iso={s.graceEndsAt} /></Text> : null}
            {s.nextNoticeAt ? <Text>{t('portal.admin.billing.nextNotice')}: <When iso={s.nextNoticeAt} /></Text> : null}
          </Panel>
        ) : null)}
      </Loadable>
    </div>
  );
}

function UsageCounters() {
  const { t } = useSurface();
  const api = useAdminApi();
  const [id, setId] = useState('');
  const [lookup, setLookup] = useState('');
  const c = useResource(`billing:usage:${lookup}`, () => (lookup ? api.usageCounter(lookup) : Promise.resolve(null)));
  return (
    <div data-testid="usage-counters">
      <form className="qx-toolbar" onSubmit={(e) => { e.preventDefault(); setLookup(id.trim()); }}>
        <Field label={t('portal.admin.billing.counterId')}>{(fid) => <input id={fid} value={id} onChange={(e) => setId(e.target.value)} />}</Field>
        <Button type="submit">{t('portal.admin.search')}</Button>
      </form>
      <Loadable resource={c}>
        {(u) => (u ? <Panel><Text data-testid="usage-used">{u.meter}: {u.used} / {u.limit} ({u.period})</Text>{u.used >= u.limit ? <Badge tone="bad">{t('portal.admin.billing.atLimit')}</Badge> : null}</Panel> : null)}
      </Loadable>
      <Text tone="muted">{t('portal.admin.billing.atomicNote')}</Text>
    </div>
  );
}
